import { useMutation } from '@apollo/client';
import { capitalCase, createError } from '@common/neo.tax';
import OneSchemaImporter, { FileUploadImportConfig, LocalImportConfig } from '@oneschema/react';
import * as Sentry from '@sentry/react';
import {
  Button,
  colors,
  ControlledAutocompleteInput,
  ControlledCheckbox,
  ControlledInput,
  ModalActions,
  ModalHeader,
  Stack,
} from '@ui-components/neo.tax';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  ADD_TIMESHEET_FILE,
  AddTimesheetFileMutation,
  AddTimesheetFileMutationVariables,
  GET_ONE_SCHEMA_JWT,
  GetOneSchemaJwtMutation,
  GetOneSchemaJwtMutationVariables,
  GET_TIMESHEET_FILE_SIGNED_UPLOAD_URL,
  GetTimesheetFileSignedUploadUrlMutation,
  GetTimesheetFileSignedUploadUrlMutationVariables,
  GET_COMPANY,
  TimesheetEffortUnit,
} from '@neo.tax/shared/graphql';
import { useCurrentUser } from '@neo.tax/shared/hooks';
import { ToastConfigState } from '@neo.tax/shared/state';
import { env, isLocalEnvironment, resolveOneSchemaTemplateKey } from '@neo.tax/shared/utils';

import { StyledPlusCircle } from '../connect-account-modal/select-providers.styled';
import { OneSchemaTimesheetModalStyled } from './one-schema-timesheet-modal.styles';

type OneSchemaTimesheetForm = {
  shouldSendToCloudStorage: boolean;
  timesheetEffortUnit: TimesheetEffortUnit | null;
  timesheetName: string;
};

type OneSchemaTimesheetModalProps = {
  close: () => void;
  companyId: string;
  isOpen: boolean;
};

export const OneSchemaTimesheetModal = ({
  close,
  companyId,
  isOpen,
}: OneSchemaTimesheetModalProps): React.ReactElement => {
  const { user } = useCurrentUser();
  const [getUploadUrlMutation] = useMutation<
    GetTimesheetFileSignedUploadUrlMutation,
    GetTimesheetFileSignedUploadUrlMutationVariables
  >(GET_TIMESHEET_FILE_SIGNED_UPLOAD_URL);
  const [getOneSchemaJwt] = useMutation<GetOneSchemaJwtMutation, GetOneSchemaJwtMutationVariables>(GET_ONE_SCHEMA_JWT);
  const [addTimesheetFile] = useMutation<AddTimesheetFileMutation, AddTimesheetFileMutationVariables>(
    ADD_TIMESHEET_FILE,
  );

  const [oneSchemaJwt, setOneSchemaJwt] = useState<string | null>(null);
  const [permanentUrl, setPermanentUrl] = useState<string | null>(null);
  const [shouldSendToCloudStorage, setShouldSendToCloudStorage] = useState<boolean | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [timesheetEffortUnit, setTimesheetEffortUnit] = useState<TimesheetEffortUnit | null>(null);
  const [timesheetName, setTimesheetName] = useState<string | null>(null);
  const [hasLaunched, setHasLaunched] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const resetState = useCallback((): void => {
    close();
    setOneSchemaJwt(null);
    setPermanentUrl(null);
    setShouldSendToCloudStorage(null);
    setSignedUrl(null);
    setTimesheetEffortUnit(null);
    setTimesheetName(null);
    setHasLaunched(false);
  }, [close]);

  const handleError = useCallback(
    (message: string, details: Record<string, unknown>): void => {
      ToastConfigState.pushErrorToast('Error from OneSchemaImporter', {
        description: message,
        duration: 'MEDIUM',
      });
      console.error(details);
      console.error(message);
      Sentry.captureException(createError('OneSchemaTimesheetModalError', message));
      resetState();
    },
    [resetState],
  );

  useEffect(() => {
    const initOneSchemaJwt = async (): Promise<void> => {
      // The `oneSchemaJwt` guard makes this fetch-once: the effect's function deps (handleError ->
      // resetState -> close) get new identities on parent re-renders, and a re-fetch after the
      // importer has launched would update its props mid-import (OneSchema ignores updated launch
      // params and warns). resetState() nulls the jwt, so a fresh open re-fetches.
      if (!isOpen || !user || oneSchemaJwt) {
        return;
      }

      const { data: oneSchemaJwtData, errors: oneSchemaJwtErrors } = await getOneSchemaJwt({
        variables: { userId: user.id },
      });
      if (!oneSchemaJwtData?.getOneSchemaJwt || oneSchemaJwtErrors) {
        handleError('getOneSchemaJwt: An unexpected error happened while fetching the one schema jwt.', {
          companyId,
          oneSchemaJwtErrors,
        });
        return;
      }

      setOneSchemaJwt(oneSchemaJwtData?.getOneSchemaJwt);
    };
    initOneSchemaJwt();
  }, [companyId, getOneSchemaJwt, handleError, isOpen, oneSchemaJwt, user]);

  useEffect(() => {
    const initUploadUrls = async (): Promise<void> => {
      // The `signedUrl` guard makes this fetch-once (see the jwt effect above). It matters for
      // correctness here: each call mints a new timestamped object path, so a re-fetch after launch
      // would record a permanentUrl that OneSchema (still uploading to the first signed URL) never
      // writes to. resetState() nulls the url, so a fresh open re-fetches.
      if (!isOpen || !timesheetEffortUnit || !timesheetName || shouldSendToCloudStorage !== true || signedUrl) {
        return;
      }

      setIsLoading(true);

      try {
        const { data: uploadUrlData, errors: uploadUrlErrors } = await getUploadUrlMutation({
          variables: { companyId, effortUnit: timesheetEffortUnit, name: timesheetName },
        });
        if (
          !uploadUrlData?.getTimesheetFileSignedUploadUrl.permanentUrl ||
          !uploadUrlData?.getTimesheetFileSignedUploadUrl.signedUrl ||
          uploadUrlErrors
        ) {
          handleError('getUploadUrlMutation: An unexpected error happened while fetching the signed url.', {
            companyId,
            timesheetEffortUnit,
            uploadUrlErrors,
          });
          setIsLoading(false);
          return;
        }
        setPermanentUrl(uploadUrlData.getTimesheetFileSignedUploadUrl.permanentUrl);
        setSignedUrl(uploadUrlData.getTimesheetFileSignedUploadUrl.signedUrl);
        setIsLoading(false);
      } catch (error) {
        const errorMessage =
          (error instanceof Object || error instanceof Error) && 'message' in error && typeof error.message === 'string'
            ? error.message
            : 'An unexpected error occurred.';
        handleError(`initUploadUrls: ${errorMessage}`, { companyId, timesheetEffortUnit });
        setIsLoading(false);
        return;
      }
    };
    initUploadUrls();
  }, [
    companyId,
    getUploadUrlMutation,
    handleError,
    isOpen,
    shouldSendToCloudStorage,
    signedUrl,
    timesheetEffortUnit,
    timesheetName,
  ]);

  const onCancel = (): void => {
    close();
    resetState();
  };

  const onError = (error: unknown): void => {
    const errorMessage =
      (error instanceof Object || error instanceof Error) && 'message' in error && typeof error.message === 'string'
        ? error.message
        : 'An unexpected error occurred.';
    handleError(`onErrorCallback: ${errorMessage}`, {
      companyId,
      oneSchemaJwt,
      permanentUrl,
      signedUrl,
      timesheetEffortUnit,
      timesheetName,
    });
  };

  const onSuccess = async (): Promise<void> => {
    if (shouldSendToCloudStorage === false) {
      resetState();
      return;
    }

    // Read into locals before resetState() nulls the underlying state. resetState() runs before
    // addTimesheetFile and works today only via closure capture; capturing explicitly keeps the
    // sync-event name + effort unit correct.
    const importName = timesheetName;
    const effortUnit = timesheetEffortUnit;
    const url = permanentUrl;

    if (!url || !importName || !effortUnit) {
      handleError('onSuccessCallback: Missing data needed for calling addTimesheetFile', {
        companyId,
        oneSchemaJwt,
        permanentUrl: url,
        signedUrl,
        timesheetEffortUnit: effortUnit,
        timesheetName: importName,
      });
      return;
    }

    resetState();

    try {
      await addTimesheetFile({
        variables: {
          companyId,
          addTimesheetFileInput: {
            permanentUrl: url,
            name: importName,
            effortUnit,
          },
        },
        // The data-sources table renders from the cached GetCompany query (company.timesheetUploads);
        // the sync event is created synchronously by this mutation, so refetch to surface it without a
        // hard refresh. awaitRefetchQueries so the success toast below only fires once the list is fresh.
        refetchQueries: [GET_COMPANY],
        awaitRefetchQueries: true,
      });

      ToastConfigState.pushSuccessToast('Timesheet import started', {
        description: `"${importName}" was uploaded and is now importing.`,
        duration: 'MEDIUM',
      });
    } catch (error) {
      const errorMessage =
        (error instanceof Object || error instanceof Error) && 'message' in error && typeof error.message === 'string'
          ? error.message
          : 'An unexpected error occurred.';
      handleError(`addTimesheet: ${errorMessage}`, {
        companyId,
        oneSchemaJwt,
        permanentUrl: url,
        signedUrl,
        timesheetEffortUnit: effortUnit,
        timesheetName: importName,
      });
    }
  };

  const { control, formState, handleSubmit, reset } = useForm<OneSchemaTimesheetForm>({
    mode: 'onChange',
    defaultValues: {
      shouldSendToCloudStorage: true,
      timesheetEffortUnit: null,
      timesheetName: '',
    },
  });

  const onSubmit = handleSubmit(
    ({ shouldSendToCloudStorage, timesheetEffortUnit, timesheetName }: OneSchemaTimesheetForm): void => {
      setShouldSendToCloudStorage(shouldSendToCloudStorage);
      setTimesheetEffortUnit(timesheetEffortUnit);
      setTimesheetName(timesheetName);
      reset();
    },
  );

  const onLaunched = useCallback((): void => {
    // Close the form modal only after OneSchema has successfully launched
    setHasLaunched(true);
  }, []);

  const isOneSchemaOpen =
    isOpen && !!oneSchemaJwt && (shouldSendToCloudStorage === false || !!(signedUrl && permanentUrl));

  const importConfig: FileUploadImportConfig | LocalImportConfig =
    shouldSendToCloudStorage === true
      ? {
          format: 'csv',
          type: 'file-upload',
          url: signedUrl ?? '',
        }
      : { type: 'local' };

  return (
    <>
      {(shouldSendToCloudStorage === null || !hasLaunched) && (
        <OneSchemaTimesheetModalStyled isOpen={isOpen}>
          <Stack as="form" direction="column" space={8}>
            <ModalHeader
              copy="Enter the custom OneSchema template name."
              icon={<StyledPlusCircle style={{ color: colors.blue[600] }} />}
              iconBgColor={colors.white}
              title=""
            />
            <ControlledInput
              control={control}
              name="timesheetName"
              id="timesheetName"
              label="Import Name"
              placeholder="e.g. FY2025 timesheets"
              helpText="A name to identify this import batch"
              rules={{
                required: 'Please provide a name for this import',
              }}
            />
            <ControlledAutocompleteInput
              control={control}
              name="timesheetEffortUnit"
              label="Effort Unit"
              helpText="The unit of effort for the timesheet"
              id="timesheetEffortUnit"
              options={Object.values(TimesheetEffortUnit).map((value) => ({
                value,
                label: capitalCase(value),
              }))}
              rules={{
                required: 'Please provide effort unit for timesheet',
              }}
            />
            <ControlledCheckbox
              control={control}
              id="shouldSendToCloudStorage"
              label="Send to Cloud Storage"
              name="shouldSendToCloudStorage"
            ></ControlledCheckbox>
            <ModalActions>
              <Button variant="white" rounded="regular" size="base" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                variant="primary"
                rounded="regular"
                size="base"
                onClick={onSubmit}
                disabled={!formState.isValid || isLoading}
              >
                {isLoading ? 'Loading...' : 'Submit'}
              </Button>
            </ModalActions>
          </Stack>
        </OneSchemaTimesheetModalStyled>
      )}
      <OneSchemaImporter
        /* managing state from your application */
        isOpen={isOneSchemaOpen}
        /* required config values */
        clientId={env.REACT_APP_ONE_SCHEMA_CLIENT_ID}
        userJwt={oneSchemaJwt ?? ''}
        templateKey={resolveOneSchemaTemplateKey('TIMESHEET-V1')}
        /* optional config values */
        importConfig={importConfig}
        devMode={isLocalEnvironment()}
        className="oneschema-importer"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1000,
        }}
        /* handling results */
        onCancel={onCancel}
        onError={onError}
        onSuccess={onSuccess}
        onLaunched={onLaunched}
      />
    </>
  );
};
